import { apiClient } from '@/api/axios';
import { endpoints } from '@/api/endpoints';
import type {
  CreateMenuRequest,
  MenuItem,
  UpdateMenuRequest,
} from '@/types/menu';

interface MenuItemResponseDto {
  product_id: string;
  menu_item_id: string;
  label_key: string;
  route: string;
  parent_menu_item_id?: string | null;
  item_type?: 'item' | 'group';
  sort_order?: number;
  icon?: string | null;
}

interface MenuItemCreateDto {
  menu_item_id: string;
  route: string;
  parent_menu_item_id?: string | null;
  item_type?: 'item' | 'group';
  label_key?: string | null;
  sort_order?: number;
  icon?: string | null;
}

interface MenuItemUpdateDto {
  label_key?: string | null;
  route?: string | null;
  parent_menu_item_id?: string | null;
  item_type?: 'item' | 'group';
  sort_order?: number | null;
  icon?: string | null;
}

interface GetMenusOptions {
  includeHierarchy?: boolean;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function ensureMenuItemDto(value: unknown): MenuItemResponseDto {
  if (!isObject(value)) {
    throw new Error(
      'Invalid menu API response: menu item payload must be an object.',
    );
  }

  if (typeof value.product_id !== 'string') {
    throw new Error('Invalid menu API response: product_id must be a string.');
  }

  if (typeof value.menu_item_id !== 'string') {
    throw new Error(
      'Invalid menu API response: menu_item_id must be a string.',
    );
  }

  if (typeof value.label_key !== 'string') {
    throw new Error('Invalid menu API response: label_key must be a string.');
  }

  if (typeof value.route !== 'string') {
    throw new Error('Invalid menu API response: route must be a string.');
  }

  if (
    value.parent_menu_item_id !== undefined &&
    value.parent_menu_item_id !== null &&
    typeof value.parent_menu_item_id !== 'string'
  ) {
    throw new Error(
      'Invalid menu API response: parent_menu_item_id must be a string or null.',
    );
  }

  if (
    value.item_type !== undefined &&
    value.item_type !== 'item' &&
    value.item_type !== 'group'
  ) {
    throw new Error(
      "Invalid menu API response: item_type must be 'item' or 'group'.",
    );
  }

  if (value.sort_order !== undefined && typeof value.sort_order !== 'number') {
    throw new Error('Invalid menu API response: sort_order must be a number.');
  }

  return {
    product_id: value.product_id,
    menu_item_id: value.menu_item_id,
    label_key: value.label_key,
    route: value.route,
    parent_menu_item_id:
      value.parent_menu_item_id === undefined
        ? null
        : value.parent_menu_item_id,
    item_type: value.item_type,
    sort_order: value.sort_order,
    icon:
      value.icon === undefined || value.icon === null
        ? value.icon
        : typeof value.icon === 'string'
          ? value.icon
          : undefined,
  };
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function mapMenuDto(dto: MenuItemResponseDto): MenuItem {
  return {
    id: dto.menu_item_id,
    label: dto.label_key,
    iframeUrl: dto.route,
    productId: dto.product_id,
    route: dto.route,
    parentMenuItemId: dto.parent_menu_item_id ?? null,
    itemType: dto.item_type ?? 'item',
    sort_order: dto.sort_order,
    icon: typeof dto.icon === 'string' && dto.icon ? dto.icon : undefined,
  };
}

function toCreateMenuDto(data: CreateMenuRequest): MenuItemCreateDto {
  const payload: MenuItemCreateDto = {
    menu_item_id: data.id?.trim() || slugify(data.label),
    route: data.route ?? data.iframeUrl ?? '',
    parent_menu_item_id: data.parentMenuItemId,
    item_type: data.itemType ?? 'item',
    label_key: data.label || null,
    sort_order: data.sort_order ?? 0,
  };

  if (data.icon) {
    payload.icon = data.icon;
  }

  return payload;
}

function toUpdateMenuDto(data: UpdateMenuRequest): MenuItemUpdateDto {
  const payload: MenuItemUpdateDto = {
    label_key: data.label,
    route: data.route ?? data.iframeUrl,
    parent_menu_item_id: data.parentMenuItemId,
    item_type: data.itemType,
    sort_order: data.sort_order,
  };

  if (data.icon) {
    payload.icon = data.icon;
  }

  return payload;
}

export async function getMenus(
  productId: string,
  options?: GetMenusOptions,
): Promise<MenuItem[]> {
  const response = await apiClient.get<MenuItemResponseDto[]>(
    endpoints.menus(productId),
    {
      params: options?.includeHierarchy
        ? { includeHierarchy: true }
        : undefined,
    },
  );
  if (!Array.isArray(response.data)) {
    throw new Error(
      'Invalid menu API response: menus payload must be an array.',
    );
  }

  return response.data.map((dto) => mapMenuDto(ensureMenuItemDto(dto)));
}

export async function getMenu(
  productId: string,
  menuItemId: string,
): Promise<MenuItem> {
  const response = await apiClient.get<MenuItemResponseDto>(
    endpoints.menuById(productId, menuItemId),
  );
  return mapMenuDto(ensureMenuItemDto(response.data));
}

export async function createMenu(
  productId: string,
  data: CreateMenuRequest,
): Promise<MenuItem> {
  const response = await apiClient.post<MenuItemResponseDto>(
    endpoints.menus(productId),
    toCreateMenuDto(data),
  );
  return mapMenuDto(response.data);
}

export async function updateMenu(
  productId: string,
  menuItemId: string,
  data: UpdateMenuRequest,
): Promise<MenuItem> {
  const response = await apiClient.patch<MenuItemResponseDto>(
    endpoints.menuById(productId, menuItemId),
    toUpdateMenuDto(data),
  );
  return mapMenuDto(response.data);
}

export async function deleteMenu(
  productId: string,
  menuItemId: string,
): Promise<void> {
  await apiClient.delete(endpoints.menuById(productId, menuItemId));
}
